import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { twMerge } from "tailwind-merge";
import { Button } from "@components/Button";
import { WalletIcon } from "@components/icons";
import { Modal } from "@components/Modal/Modal";
import { useFeatureConfig } from "@hooks/useFeatureFlags";
import { useModal } from "@hooks/useModal";
import { type IUseModalProps } from "@store/modal/modal";
import { useUpdatePaymentTaxCollectorTypeMutation } from "@services/payments/mutations";
import { usePaymentTaxCollectorTypes } from "@services/payments/queries";
import {
  TaxCollectorCategory,
  type TaxCollectorTypeId,
} from "@services/payments/types";
import { isNullOrUndefined } from "@utils/common";
import { TaxCollectorType } from "./components/TaxCollectorType";
import { TaxCollectorTypeSection } from "./components/TaxCollectorTypeSection";

export const PaymentTaxCollectorSelectModal: React.FC<IUseModalProps> = (
  props,
) => {
  const modal = useModal(props);
  const { t } = useTranslation();
  const { isTaxCollectorCategoryGroupingEnabled } = useFeatureConfig();

  const query = usePaymentTaxCollectorTypes();
  const { mutateAsync: updatePaymentTaxCollectorType, isPending } =
    useUpdatePaymentTaxCollectorTypeMutation();

  const [selectedTaxCollectorTypeId, setSelectedTaxCollectorTypeId] =
    useState<TaxCollectorTypeId>();

  useEffect(() => {
    if (query.data && !selectedTaxCollectorTypeId) {
      const checkedTaxCollectorType = query.data.taxCollectorTypes.find(
        (taxCollectorType) => taxCollectorType.isChecked,
      );
      if (!isNullOrUndefined(checkedTaxCollectorType?.taxCollectorTypeId)) {
        setSelectedTaxCollectorTypeId(
          checkedTaxCollectorType.taxCollectorTypeId,
        );
      }
    }
  }, [query.data, selectedTaxCollectorTypeId]);

  const changeTaxCollector = async () => {
    if (selectedTaxCollectorTypeId) {
      const selectedTaxCollectorType = query?.data?.taxCollectorTypes.find(
        (type) => type.taxCollectorTypeId === selectedTaxCollectorTypeId,
      );
      if (
        selectedTaxCollectorType &&
        selectedTaxCollectorType?.notMetRequirements.length > 0
      ) {
        toast.warning(`${selectedTaxCollectorType.name}: 
          ${t(
            `taxCollectorRequirement.${selectedTaxCollectorType?.notMetRequirements[0]}`,
          )}`);

        return;
      }

      const response = await updatePaymentTaxCollectorType(
        selectedTaxCollectorTypeId,
      ).catch(() => {
        toast.error(t("toast.error"));
      });

      if (!isNullOrUndefined(response)) {
        toast.success(t("paymentSelectModal.changeSuccess"));
        modal.close();
      }
    }
  };

  return (
    <Modal
      modal={modal}
      actionButtons={
        <div className="flex w-full justify-between gap-3">
          <Button
            variant="secondary"
            className="w-full md:w-fit"
            onClick={modal.close}
            aria-label={t("common.form.buttons.cancel")}
          >
            {t("common.form.buttons.cancel")}
          </Button>
          <Button
            className="w-full md:w-fit"
            onClick={changeTaxCollector}
            isLoading={isPending}
            aria-label={t("common.form.buttons.continue")}
          >
            {t("common.form.buttons.continue")}
          </Button>
        </div>
      }
      title={t("paymentSelectModal.title")}
      subtitle={t("paymentSelectModal.subtitle")}
      badgeProps={{ icon: WalletIcon }}
      className={twMerge(
        "md:max-w-[32rem]",
        isTaxCollectorCategoryGroupingEnabled && "md:max-w-[46rem]",
      )}
    >
      {isTaxCollectorCategoryGroupingEnabled ? (
        Object.entries(TaxCollectorCategory)
          .filter((category) =>
            query.data?.taxCollectorTypes.some(
              (type) => type.category === category[1],
            ),
          )
          .map(([key, value]) => (
            <TaxCollectorTypeSection
              key={key}
              taxCollectorTypes={query.data?.taxCollectorTypes}
              selectedId={selectedTaxCollectorTypeId}
              onSelect={(taxCollectorType) =>
                setSelectedTaxCollectorTypeId(
                  taxCollectorType.taxCollectorTypeId,
                )
              }
              category={value}
            />
          ))
      ) : (
        <div className="flex flex-col flex-wrap gap-4 md:flex-row">
          {query.data?.taxCollectorTypes.map((paymentTaxCollectorType) => (
            <TaxCollectorType
              key={paymentTaxCollectorType.taxCollectorTypeId}
              className="basis-[calc(50%-0.5rem)]"
              selectedId={selectedTaxCollectorTypeId}
              onSelect={(taxCollectorType) =>
                setSelectedTaxCollectorTypeId(
                  taxCollectorType.taxCollectorTypeId,
                )
              }
              taxCollectorType={paymentTaxCollectorType}
            />
          ))}
        </div>
      )}
    </Modal>
  );
};
